import { useEffect, useState } from 'react'
import { FiAlertTriangle, FiX, FiCheck, FiXCircle } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'

interface ConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info'
  isLoading?: boolean
  requireAcknowledgement?: boolean
  acknowledgementLabel?: string
}

export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'danger',
  isLoading = false,
  requireAcknowledgement = false,
  acknowledgementLabel
}: ConfirmationDialogProps) {
  const { language } = useLanguageStore()
  const isRTL = language === 'ar'
  const [acknowledged, setAcknowledged] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, isLoading, onClose])

  if (!isOpen) return null

  const handleConfirm = () => {
    if (!isLoading && (!requireAcknowledgement || acknowledged)) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    if (!isLoading) {
      onClose()
    }
  }

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          icon: <FiAlertTriangle className="w-8 h-8" />,
          confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          gradient: 'from-red-500 to-red-600'
        }
      case 'warning':
        return {
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          icon: <FiAlertTriangle className="w-8 h-8" />,
          confirmBtn: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
          gradient: 'from-yellow-500 to-yellow-600'
        }
      case 'info':
        return {
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          icon: <FiAlertTriangle className="w-8 h-8" />,
          confirmBtn: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          gradient: 'from-blue-500 to-blue-600'
        }
      default:
        return {
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          icon: <FiAlertTriangle className="w-8 h-8" />,
          confirmBtn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          gradient: 'from-red-500 to-red-600'
        }
    }
  }

  const styles = getTypeStyles()

  const defaultTitle = language === 'ar' ? 'تأكيد العملية' : 'Confirm Action'
  const defaultConfirmText = language === 'ar' ? 'موافقة' : 'Confirm'
  const defaultCancelText = language === 'ar' ? 'إلغاء' : 'Cancel'

  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center">
      {/* Backdrop with blur */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleCancel}
      />
      
      {/* Dialog */}
      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${styles.gradient} text-white rounded-t-2xl p-6 ${isRTL ? 'text-right' : 'text-left'}`}>
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold">
              {title || defaultTitle}
            </h3>
            {!isLoading && (
              <button
                onClick={handleCancel}
                className="text-white/90 hover:text-white hover:bg-white/20 rounded-full p-1 transition-all duration-200"
                aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                <FiX className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`${styles.iconBg} rounded-full p-4 animate-pulse`}>
              <div className={styles.iconColor}>
                {styles.icon}
              </div>
            </div>
          </div>

          {/* Message */}
          <div className={`text-center mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
              {message}
            </p>
          </div>

          {requireAcknowledgement && (
            <label className={`flex items-start gap-3 mb-8 p-3 rounded-lg bg-red-50 border border-red-200 cursor-pointer ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span className="text-sm font-medium text-red-800">
                {acknowledgementLabel || (language === 'ar'
                  ? 'أوافق على تحمل مسؤولية حذف هذا القيد، حتى لو كان مرتبطاً بفاتورة أو مستند آخر'
                  : 'I accept responsibility for deleting this journal entry, even if it is linked to an invoice or another document')}
              </span>
            </label>
          )}

          {/* Buttons */}
          <div className={`flex gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                isRTL ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              <FiXCircle className="w-5 h-5" />
              {cancelText || defaultCancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || (requireAcknowledgement && !acknowledged)}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold text-white ${styles.confirmBtn} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl ${
                isRTL ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{language === 'ar' ? 'جاري المعالجة...' : 'Processing...'}</span>
                </>
              ) : (
                <>
                  <FiCheck className="w-5 h-5" />
                  {confirmText || defaultConfirmText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


